import {create} from "zustand";
import {clearSessionAvatar} from "../util/avatarUtils";
import {dropStorage, getStorage, setStorage} from "./util.store";
import {decodeMyToken, isTokenExpired} from "./util.token";

// Store query client reference to clear cache on logout
let queryClient = null;

export const setQueryClient = client => {
  queryClient = client;
};

export const useAuthStore = create((set, get) => ({
  authStatus: "checking",
  token: undefined,
  user: undefined,
  showAuthModal: false,
  authForm: "login",
  handleAuthForm: form => {
    set({authForm: form});
  },
  handleShowAuthModal: () => {
    const showAuthModal = !get().showAuthModal;
    set({showAuthModal});
  },
  validateSession: async () => {
    const token = getStorage("token");

    if (!token) {
      set({
        authStatus: "unauthenticated",
        token: undefined,
        user: undefined,
      });
      return;
    }

    const tokenPayload = decodeMyToken(token);
    const isTokenExp = isTokenExpired(tokenPayload);

    if (isTokenExp || !tokenPayload) {
      set({
        authStatus: "unauthenticated",
        token: undefined,
        user: undefined,
      });
      return;
    }

    set({
      authStatus: "authenticated",
      token: token.token,
      user: tokenPayload,
    });
  },
  login: async ({data}) => {
    const {access_token} = data;
    if (access_token) {
      const tokenPayload = decodeMyToken(access_token);

      // Clear any cached user data before setting new user
      if (queryClient) {
        // Clear all userData queries including user-specific ones
        queryClient.removeQueries({queryKey: ["userData"]});
        // Also clear wallet and other user-specific data
        queryClient.removeQueries({queryKey: ["userWallet"]});
        queryClient.removeQueries({queryKey: ["userMobileAccount"]});
      }

      set({
        authStatus: "authenticated",
        token: access_token,
        user: tokenPayload,
        showAuthModal: false,
      });
      setStorage("token", access_token, 0);

      // Retry FCM token registration if there's a pending token (native app)
      try {
        const {retryTokenRegistration} = await import("../services/nativeBridge.service");
        await retryTokenRegistration();
      } catch (_error) {}

      // Note: Lotoven redirect is now handled by ProtectedHomePage component
    }
  },
  logout: async () => {
    // Check if user came from mobile login
    const isMobileLoginUser = getStorage("isMobileLoginUser") === "true";

    // Note: We keep the FCM token registered even after logout
    // This allows sending notifications about freespins, prizes, promotions
    // even when user is not actively logged in

    dropStorage("modalSessionInfoShown");
    dropStorage("token");
    const _checkExtra = getStorage("token");
    // Clear session avatar when logging out
    clearSessionAvatar();

    // Clear all React Query cache when logging out
    if (queryClient) {
      queryClient.clear();
    }

    set({
      authStatus: "unauthenticated",
      token: undefined,
      user: undefined,
    });

    // Redirect mobile users back to mobile login
    if (isMobileLoginUser) {
      // Use a small delay to ensure state is updated
      setTimeout(() => {
        window.location.href = "/mobile-login";
      }, 100);
    }
  },
}));
